import { getUser } from "@utils";

export const SLIDER_PREVIEW_LIMIT = 10;

export const getContainerTopic = (container, languageCode = "en") => {
	const topic = container?.topic?.[languageCode] || container?.topic?.en;
	if (!topic) return "";

	const username = getUser()?.username;
	return topic.includes("{user}") ? topic.replace("{user}", username || "") : topic;
};

const defaultContainerGameSorter = (container) => (a, b) => {
	if (container?.type === "userLastPlayed") return -1;
	if (container?.type === "mostPlayed" && window.TENANT !== "betto") {
		return (b?.count ?? 0) - (a?.count ?? 0);
	}
	return (a?.order ?? 0) - (b?.order ?? 0);
};

export const sortContainerGames = (container, customSort) => {
	const games = [...(container?.gameList || [])];
	const sorter = customSort || defaultContainerGameSorter(container);
	return games.sort(sorter);
};

export const buildContainerViewAllLink = (container, tab, containerPage = "Casino") => {
	const tabParam = tab?.toLowerCase() === "livecasino" ? "livecasino" : "casino";
	const title = getContainerTopic(container);
	const name = encodeURIComponent(title || "collection");
	const page = encodeURIComponent(containerPage);

	return `/provider?key=container&containerId=${container.id}&name=${name}&tab=${tabParam}&containerPage=${page}&layout=browse`;
};

export const buildContainerQueryParams = (page, username) => {
	const params = { page };
	if (username) params.username = username;
	return params;
};

export const mergeContainerLists = (...lists) => {
	const byId = new Map();

	lists.forEach((list) => {
		(list || []).forEach((container) => {
			if (container?.id == null) return;
			byId.set(String(container.id), container);
		});
	});

	return Array.from(byId.values());
};

export const findContainerInSources = (containerId, ...lists) => {
	const targetId = String(containerId);

	for (const list of lists) {
		const match = (list || []).find((container) => String(container.id) === targetId);
		if (match) return match;
	}

	return null;
};

export const findContainerById = (containers, containerId) =>
	(containers || []).find((container) => String(container.id) === String(containerId));

const NEW_GAMES_TOPIC_MATCHERS = ["new game", "new games", "new added"];

const MOST_POPULAR_TOPIC_MATCHERS = [
	"most popular",
	"en popüler",
	"en populer",
	"popular games",
	"juegos más populares",
	"jogos mais populares",
];

export const findMostPopularContainer = (containers, languageCode = "en") => {
	const filtered = filterContainersByUser(containers, getUser()?.username).filter(
		(container) => container?.isActive && container?.gameList?.length
	);

	const matched = filtered.find((container) => {
		const topic = getContainerTopic(container, languageCode).toLowerCase();
		if (MOST_POPULAR_TOPIC_MATCHERS.some((matcher) => topic.includes(matcher))) {
			return true;
		}

		const englishTopic = getContainerTopic(container, "en").toLowerCase();
		return MOST_POPULAR_TOPIC_MATCHERS.some((matcher) => englishTopic.includes(matcher));
	});

	return matched || filtered[0] || null;
};

export const findNewGamesContainer = (containers, languageCode = "en") =>
	filterContainersByUser(containers, getUser()?.username).find((container) => {
		if (!container?.isActive || !container?.gameList?.length) return false;
		if (container.type === "aiNewGamesForYou") return true;

		const topic = getContainerTopic(container, languageCode).toLowerCase();
		if (NEW_GAMES_TOPIC_MATCHERS.some((matcher) => topic.includes(matcher))) return true;

		const englishTopic = getContainerTopic(container, "en").toLowerCase();
		return NEW_GAMES_TOPIC_MATCHERS.some((matcher) => englishTopic.includes(matcher));
	});

export const getActiveContainerId = (location) => {
	const searchParams = new URLSearchParams(location?.search ?? "");
	if (searchParams.get("key") !== "container") return null;
	const containerId = searchParams.get("containerId");
	return containerId ? String(containerId) : null;
};

export const getActiveContainerName = (location) => {
	const searchParams = new URLSearchParams(location?.search ?? "");
	if (searchParams.get("key") !== "container") return null;
	const name = searchParams.get("name");
	if (!name) return null;

	try {
		return decodeURIComponent(name);
	} catch {
		return name;
	}
};

export const isContainerFilterItemActive = (containerItem, location) => {
	if (!containerItem || !location) return false;

	const activeContainerId = getActiveContainerId(location);
	if (activeContainerId && String(containerItem.key) === activeContainerId) {
		return true;
	}

	const activeName = getActiveContainerName(location);
	if (!activeName || !containerItem.value) return false;

	return containerItem.value.trim() === activeName.trim();
};

export const filterContainersByUser = (containers, username) => {
	if (!containers?.length) return [];

	const sorted = [...containers].sort((a, b) => a.order - b.order);

	if (username) return sorted;

	return sorted.filter((container) => {
		if (!container?.topic) return true;
		return !Object.values(container.topic).some((topic) => topic.includes("{user}"));
	});
};

export const mapContainersToFilterItems = (
	containers,
	languageCode,
	containerPage = "Casino",
	visibilityKey = "showInFilters"
) =>
	filterContainersByUser(containers, getUser()?.username)
		.filter((container) => {
			if (!container?.isActive || !container?.gameList?.length) return false;
			if (window.TENANT === "betto" && visibilityKey) {
				return container?.configs?.[visibilityKey] !== false;
			}
			return true;
		})
		.map((container) => ({
			key: String(container.id),
			value: getContainerTopic(container, languageCode),
			iconUrl: container.icon_url,
			containerPage,
			container,
		}));
